import _ from 'underscore';
import React, { useContext } from 'react';
import { DirectionalHint } from '@fluentui/react';
import { Body1, Image } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { defaultUserGovernanceConfig } from '@bingads-webui-campaign-react/multi-step-teaching-bubble';
import { getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { getStyles } from './styles';
import CreateReportFreImg from './media/create-report.png';
import CustomizeColumnFreImg from './media/customize-column.png';
import SaveReportFreImg from './media/save-report.png';
import SaveReportLevelFreImg from './media/save-report-level.png';

const CreateReportFreContent = () => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  return (
    <div className={classes.createReportFre}>
      <Image
        src={CreateReportFreImg}
        alt={i18n.getString(_TL_('Report grid example'))}
        fit="cover"
      />
      <div className="text">
        <Body1 block>{i18n.getString(_TL_('Build a custom report from scratch by choosing the breakdowns and metrics you need using the column modifier. You can customize any report to align with your specific account requirements.'))}</Body1>
      </div>
    </div>);
};

const CustomizeColumnsFreContent = () => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  return (
    <div className={classes.customizeColumnsFre}>
      <Image
        src={CustomizeColumnFreImg}
        alt={i18n.getString(_TL_('Column chooser example'))}
        fit="cover"
      />
      <div className="text">
        <Body1 block>{i18n.getString(_TL_('Use the column modifier to add, reorder, or remove breakdowns and metrics, which are now conveniently sub-categorized.'))}</Body1>
        <ul>
          <li>{i18n.getString(_TL_('Metrics are the key performance indicators you are interested in, like impressions, clicks, or conversions, for example.'))}</li>
          <li>{i18n.getString(_TL_('Breakdowns are the categories you want to group performance by, like device type, region, or campaign type, for example.'))}</li>
        </ul>
      </div>
    </div>);
};

const SaveReportFreContent = () => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const isInternalUser = _.result(serverContext, ['CurrentUser', 'IsInternal'], false);
  const internalUserText = i18n.getString(_TL_('Once you have customized your report, you can save it for future use and schedule it to be delivered by email on a regular basis.'));
  const externalUserText = i18n.getString(_TL_('Once you have customized your report, you can save it for future use and schedule it to be delivered by email on a regular basis. Reports are saved at the account level and can be shared with others who have access to the same account.'));

  return (
    <div className={classes.saveReportFre}>
      <Image
        src={SaveReportFreImg}
        alt={i18n.getString(_TL_('Save report example'))}
        fit="cover"
      />
      <div className="text">
        <Body1 block>{isInternalUser ? internalUserText : externalUserText}</Body1>
      </div>
    </div>);
};

const SaveReportLevelFreContent = () => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  return (
    <div className={classes.saveReportLvelFre}>
      <Image
        src={SaveReportLevelFreImg}
        alt={i18n.getString(_TL_('Save report at account or user level example'))}
        fit="cover"
      />
      <div className="text">
        <Body1 block>{i18n.getString(_TL_('You have the option to save the report at the Account level (so others with access can view it) or at the Account & User level (so you can reuse it across multiple accounts you manage).'))}</Body1>
      </div>
    </div>);
};

export const ReportHomePageTeachingBubbleConfig = {
  featureName: 'report-home-page',
  userGovernanceConfig: defaultUserGovernanceConfig,
  steps: [
    {
      title: _TL_('Create a new report'),
      content: <CreateReportFreContent />,
      primaryButtonText: _TL_('Try now'),
      secondaryButtonText: _TL_('Try later'),
      targetElement: '.reporting-landing-page #button-create',
      directionalHint: DirectionalHint.rightCenter,
      onNext: (_scope, _siteMap) => {
        _scope.save(getRoutePathById(_siteMap, 'reporting-build-custom-report'));
      },
    },
  ],
};

export const CustomReportBuilderTeachingBubbleConfig = {
  featureName: 'custom-report-builder',
  userGovernanceConfig: defaultUserGovernanceConfig,
  steps: [
    {
      title: _TL_('Customize your columns'),
      content: <CustomizeColumnsFreContent />,
      primaryButtonText: _TL_('Next'),
      secondaryButtonText: _TL_('Remind me later'),
      targetElement: '.breakdown-container button',
      directionalHint: DirectionalHint.rightCenter,
    },
    {
      title: _TL_('Save and schedule your custom report'),
      content: <SaveReportFreContent />,
      primaryButtonText: _TL_('Done'),
      secondaryButtonText: _TL_('Remind me later'),
      targetElement: '#SaveAsButton .fui-SplitButton__primaryActionButton',
      directionalHint: DirectionalHint.leftCenter,
    },
  ],
};

export const ReportSaveAtUserLevelTeachingBubbleConfig = {
  featureName: 'report-save-at-user-level',
  userGovernanceConfig: defaultUserGovernanceConfig,
  steps: [
    {
      title: _TL_('Save and share your custom report'),
      content: <SaveReportLevelFreContent />,
      primaryButtonText: _TL_('Done'),
      secondaryButtonText: _TL_('Remind me later'),
      targetElement: '.store-level-selector',
      directionalHint: DirectionalHint.rightCenter,
    },
  ],
};
